/**
 * @author       julian@zumper.com (Julian Napolitan)
 * @copyright    Copyright (c) 2021, Zumper
 */

import { PrimaryButton } from '@zcode-ui/controls'
import { selectToggleValue } from '@zcode-ui/filters'
import { debounce } from '@zcode/utils'
import React, { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { NAVBAR_MOBILE_LIST } from '../../../constants'
import { getMatchingCount } from '../../../inspectors'
import { selectIsListablesFetching } from '../../../module/searchUi/selectors'
import { LoadingIndicator } from '../LoadingIndicator'
import * as styles from './style'

interface Props {
  intersectionRefOptions?: { inView: boolean; entry: any }
  isPinFeatureEnabled?: boolean
  onClick: () => void
  shimmer: boolean
  shouldHide?: boolean
  viewType: string
}

export const MapListToggle = ({
  intersectionRefOptions,
  isPinFeatureEnabled,
  onClick,
  shimmer,
  viewType,
}: Props) => {
  const toggleBtnRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const matchingCount = useSelector(getMatchingCount)
  const isListablesFetching = useSelector(selectIsListablesFetching)
  const toggleValue = useSelector(selectToggleValue)

  const { inView } = intersectionRefOptions ?? {}

  useEffect(() => {
    let unmounted = false
    const windowOffsetTop = window?.pageYOffset
    const { entry } = intersectionRefOptions ?? {}

    if (!unmounted && toggleBtnRef && toggleBtnRef.current) {
      const getFixedOnScroll = debounce(() => {
        if (windowOffsetTop >= 0 && entry?.target?.offsetTop && toggleBtnRef.current) {
          if (inView && windowOffsetTop < entry?.target?.offsetTop) {
            toggleBtnRef.current.style.position = 'fixed'
            toggleBtnRef.current.style.bottom = '30px'
            toggleBtnRef.current.style.display = 'block'
          }
        }
      }, 50)

      getFixedOnScroll()
      window.addEventListener('scroll', getFixedOnScroll)

      return () => {
        unmounted = true
        window.removeEventListener('scroll', getFixedOnScroll)
      }
    }
  }, [intersectionRefOptions, inView])

  if (shimmer || !!toggleValue || (!inView && viewType === NAVBAR_MOBILE_LIST)) {
    return null
  }

  return (
    <div css={styles.toggleBtnParent} ref={toggleBtnRef}>
      <div css={styles.toggleContainer} ref={containerRef}>
        {isPinFeatureEnabled && isListablesFetching ? (
          <LoadingIndicator />
        ) : (
          <PrimaryButton css={styles.toggleBtn} onClick={onClick}>
            {viewType === NAVBAR_MOBILE_LIST
              ? 'Map'
              : isPinFeatureEnabled
                ? `${matchingCount} rental${matchingCount > 1 ? 's' : ''}`
                : 'List'}
          </PrimaryButton>
        )}
      </div>
    </div>
  )
}
